import moment from 'moment';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { 
  BOARD_LOAD, 
  BOARD_MOVE_ITEM,
  BOARD_DUPLICATE_ITEM, 
  SET_BOARD_TYPE, 
  SET_PERIOD,
  BOARD_TYPES
} from './actions';
import { DEFAULT_COLUMNS, BOARD_ID_MAP } from './constants';
import * as drawerAcions from './components/Drawer/actions';

// Get default columns for board type
const getDefaultColumns = (boardType) => {
  return DEFAULT_COLUMNS[boardType] || DEFAULT_COLUMNS[BOARD_TYPES.MISSOES];
};

const defaultState = {
  data: {
    1: {
      id: 1,
      name: 'Missões',
      uid: 'uid-1',
      lists: getDefaultColumns(BOARD_TYPES.MISSOES)
    },
    2: {
      id: 2,
      name: 'Convites',
      uid: 'uid-2',
      lists: getDefaultColumns(BOARD_TYPES.CONVITES)
    }
  },
  currentBoardType: BOARD_TYPES.MISSOES,
  selectedDate: moment().format('MM-YYYY'),
  currentPeriod: {
    month: moment().month() + 1,
    year: moment().year(),
    isCurrent: true
  },
  meta: null,
  needsReload: false,
  lastAction: null
};

const ACTION_HANDLERS = {
  [CACHE_ACTIONS.marketing]: (state, action) => {
    // Always reset to current month when loading from cache
    const currentDate = moment();
    return {
      ...action.payload,
      selectedDate: currentDate.format('MM-YYYY'),
      currentPeriod: {
        month: currentDate.month() + 1,
        year: currentDate.year(),
        isCurrent: true
      }
    };
  },

  [SET_BOARD_TYPE]: (state, action) => {
    const newState = {
      ...state,
      currentBoardType: action.payload
    };

    // Cache the updated state after board type change
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });

    return newState;
  },

  [SET_PERIOD]: (state, action) => {
    const now = moment();
    const selectedPeriod = moment({ 
      month: action.payload.month - 1, 
      year: action.payload.year 
    });
    
    const newState = {
      ...state,
      currentPeriod: {
        month: action.payload.month,
        year: action.payload.year,
        isCurrent: selectedPeriod.isSame(now, 'month')
      }
    };

    // Cache the updated state after period change
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });

    return newState;
  },


  [BOARD_MOVE_ITEM.INIT]: (state, action) => {
    const boardId = BOARD_ID_MAP[state.currentBoardType];
    const currentBoard = state.data[boardId];
    const data = action.payload.request.data;
    const sourceColumnId = parseInt(data.source.droppableId);
    const destColumnId = parseInt(data.destination.droppableId);
    
    // Find the card being moved
    const sourceCards = currentBoard.lists[sourceColumnId].cards;
    const cardToMove = sourceCards.find(card => card.uid === data.draggableId);
    
    if (!cardToMove) return state;

    const updatedLists = Object.keys(currentBoard.lists).reduce((acc, key) => {
      const columnId = parseInt(key);
      const column = currentBoard.lists[columnId];
      
      if (columnId === sourceColumnId) {
        // Remove card from source column
        acc[columnId] = {
          ...column,
          cards: column.cards.filter(card => card.uid !== data.draggableId)
        };
      } else if (columnId === destColumnId) {
        // Add card to destination column
        const updatedCard = { ...cardToMove, column: columnId };
        const newCards = [...column.cards];
        newCards.splice(data.destination.index, 0, updatedCard);
        acc[columnId] = {
          ...column,
          cards: newCards
        };
      } else {
        // Keep other columns unchanged
        acc[columnId] = column;
      }
      
      return acc;
    }, {});

    const newState = {
      ...state,
      data: {
        ...state.data,
        [boardId]: {
          ...currentBoard,
          lists: updatedLists
        }
      }
    };

    // Cache the updated state after item move
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });

    return newState;
  },

  // Handle drawer actions for item creation/updates  
  [drawerAcions.CREATE.SUCCESS]: (state, action) => {
    // Get the newly created item from the API response
    const newItem = action.payload && action.payload.data && action.payload.data.data;
    const boardType = action.payload && action.payload.boardType || state.currentBoardType;
    const boardId = BOARD_ID_MAP[boardType];
    const currentBoard = state.data && state.data[boardId];
    
    if (!newItem || !currentBoard || !currentBoard.lists) {
      // If we don't have the data structure, just mark for reload
      return {
        ...state,
        needsReload: true,
        lastAction: 'CREATE'
      };
    }
    
    // Find the first column (usually column 1)
    const firstColumnId = Object.keys(currentBoard.lists).sort((a, b) => parseInt(a) - parseInt(b))[0];
    
    if (!firstColumnId) {
      // If no columns exist, just mark for reload
      return {
        ...state,
        needsReload: true,
        lastAction: 'CREATE'
      };
    }
    
    // Create the new card object with proper structure
    const newCard = {
      id: newItem.id,
      uid: newItem.uid || newItem.id.toString(),
      title: newItem.title,
      description: newItem.description,
      tasks: newItem.tasks || [],
      board_id: newItem.board_id,
      column: parseInt(firstColumnId),
      created_at: newItem.created_at,
      updated_at: newItem.updated_at
    };
    
    // Add the new item to the top of the first column
    const updatedLists = {
      ...currentBoard.lists,
      [firstColumnId]: {
        ...currentBoard.lists[firstColumnId],
        cards: [newCard, ...(currentBoard.lists[firstColumnId].cards || [])]
      }
    };
    
    const newState = {
      ...state,
      data: {
        ...state.data,
        [boardId]: {
          ...currentBoard,
          lists: updatedLists
        }
      },
      needsReload: true, // Still mark for reload to sync with server
      lastAction: 'CREATE'
    };
    
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });
    
    return newState;
  },

  [drawerAcions.SAVE.SUCCESS]: (state, action) => {
    // Mark that data needs to be reloaded
    const newState = {
      ...state,
      needsReload: true,
      lastAction: 'SAVE'
    };
    
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });
    
    return newState;
  },

  [drawerAcions.DELETE.SUCCESS]: (state, action) => {
    // Get the deleted item data
    const deletedItem = action.payload && action.payload.data;
    const boardType = action.payload && action.payload.boardType || state.currentBoardType;
    const boardId = BOARD_ID_MAP[boardType];
    const currentBoard = state.data && state.data[boardId];
    
    if (!deletedItem || !currentBoard || !currentBoard.lists) {
      // If we don't have the data structure, just mark for reload
      return {
        ...state,
        needsReload: true,
        lastAction: 'DELETE'
      };
    }
    
    // Remove the item from all columns (in case it moved)
    const updatedLists = Object.keys(currentBoard.lists).reduce((acc, columnId) => {
      const column = currentBoard.lists[columnId];
      acc[columnId] = {
        ...column,
        cards: (column.cards || []).filter(card => 
          card.id !== deletedItem.id && card.uid !== deletedItem.uid
        )
      };
      return acc;
    }, {});
    
    const newState = {
      ...state,
      data: {
        ...state.data,
        [boardId]: {
          ...currentBoard,
          lists: updatedLists
        }
      },
      needsReload: true, // Still mark for reload to sync with server
      lastAction: 'DELETE'
    };
    
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });
    
    return newState;
  },

  [BOARD_DUPLICATE_ITEM.SUCCESS]: (state, action) => {
    // Get the duplicated item from the API response
    const duplicatedItem = action.payload && action.payload.data && action.payload.data.data;
    const boardType = action.payload && action.payload.boardType || state.currentBoardType;
    const boardId = BOARD_ID_MAP[boardType];
    const currentBoard = state.data && state.data[boardId];
    
    if (!duplicatedItem || !currentBoard || !currentBoard.lists) {
      // If we don't have the data structure, just mark for reload
      return {
        ...state,
        needsReload: true,
        lastAction: 'DUPLICATE'
      };
    }
    
    // Get the column where the duplicated item should be added
    const targetColumnId = duplicatedItem.column || Object.keys(currentBoard.lists).sort((a, b) => parseInt(a) - parseInt(b))[0];
    
    if (!targetColumnId || !currentBoard.lists[targetColumnId]) {
      // If no column exists, just mark for reload
      return {
        ...state,
        needsReload: true,
        lastAction: 'DUPLICATE'
      };
    }
    
    // Create the duplicated card object with proper structure
    const duplicatedCard = {
      id: duplicatedItem.id,
      uid: duplicatedItem.uid || duplicatedItem.id.toString(),
      title: duplicatedItem.title,
      description: duplicatedItem.description,
      tasks: duplicatedItem.tasks || [],
      board_id: duplicatedItem.board_id,
      column: parseInt(targetColumnId),
      is_revised: duplicatedItem.is_revised,
      created_at: duplicatedItem.created_at,
      updated_at: duplicatedItem.updated_at
    };
    
    // Add the duplicated item to the target column (at the top)
    const updatedLists = {
      ...currentBoard.lists,
      [targetColumnId]: {
        ...currentBoard.lists[targetColumnId],
        cards: [duplicatedCard, ...(currentBoard.lists[targetColumnId].cards || [])]
      }
    };
    
    const newState = {
      ...state,
      data: {
        ...state.data,
        [boardId]: {
          ...currentBoard,
          lists: updatedLists
        }
      },
      needsReload: true, // Still mark for reload to sync with server
      lastAction: 'DUPLICATE'
    };
    
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });
    
    return newState;
  },

  // Clear reload flag when board data is loaded
  [BOARD_LOAD.SUCCESS]: (state, action) => {
    const response = action.payload.data;
    
    // Extract boardType from different possible locations
    let boardType = action.payload.boardType || 
                   action.boardType || 
                   (action.meta && action.meta.boardType) ||
                   (action.payload.meta && action.payload.meta.boardType);
    
    // If still no boardType, try to determine from the response
    if (!boardType && response && response.meta && response.meta.board_info && response.meta.board_info.type) {
      boardType = response.meta.board_info.type;
    }
    
    // Final fallback
    if (!boardType) {
      boardType = 'missoes';
    }
    
    const month = action.payload.month;
    const year = action.payload.year;
    
    // Update selectedDate if period params are provided
    let selectedDate = state.selectedDate;
    if (month && year) {
      selectedDate = `${month.toString().padStart(2, '0')}-${year}`;
    }
    
    // Handle the new API structure with response.data.data.1
    let boardData;
    const boardId = BOARD_ID_MAP[boardType];
    
    // Check the new API structure: response.data.data.[boardId]
    if (response && response.data && response.data.data && response.data.data[boardId.toString()]) {
      // Get the raw board data from API
      const rawBoardData = response.data.data[boardId.toString()];
      
      // Ensure the board data structure is preserved correctly
      boardData = {
        id: rawBoardData.id,
        name: rawBoardData.name,
        uid: rawBoardData.uid || `uid-${boardId}`,
        lists: {}
      };
      
      // Process each list and preserve cards
      Object.keys(rawBoardData.lists).forEach(listId => {
        const list = rawBoardData.lists[listId];
        
        boardData.lists[listId] = {
          id: list.id,
          uid: list.uid || listId,
          name: list.name,
          style: list.style,
          cards: list.cards || [] // Ensure cards array is preserved
        };
      });
      
    } else if (response && response.data && Array.isArray(response.data)) {
      // Fallback for array response (older structure)
      const defaultColumns = getDefaultColumns(boardType);
      const updatedLists = Object.keys(defaultColumns).reduce((acc, key) => {
        const list = defaultColumns[key];
        acc[list.id] = {
          ...list,
          cards: response.data.filter(card => card.column === list.id)
        };
        return acc;
      }, {});

      boardData = {
        id: boardId,
        name: boardType === BOARD_TYPES.CONVITES ? 'Convites' : 'Missões',
        uid: `uid-${boardId}`,
        lists: updatedLists
      };
    } else {
      // Use default structure if no data
      boardData = {
        id: boardId,
        name: boardType === BOARD_TYPES.CONVITES ? 'Convites' : 'Missões', 
        uid: `uid-${boardId}`,
        lists: getDefaultColumns(boardType)
      };
    }

    // Update period info from meta if available
    let periodInfo = state.currentPeriod;
    if (response && response.meta && response.meta.period) {
      periodInfo = {
        month: response.meta.period.month,
        year: response.meta.period.year,
        isCurrent: response.meta.period.is_current
      };
    } else if (month && year) {
      const now = moment();
      const selectedPeriod = moment({ month: month - 1, year: year });
      periodInfo = {
        month: month,
        year: year,
        isCurrent: selectedPeriod.isSame(now, 'month')
      };
    }

    const newState = {
      ...state,
      data: {
        ...state.data,
        [boardId]: boardData
      },
      currentBoardType: boardType,
      selectedDate: selectedDate,
      currentPeriod: periodInfo,
      meta: (response && response.meta) || null,
      needsReload: false, // Clear reload flag
      lastAction: null
    };
    
    
    

    // Cache the updated state
    LocalCacheHandler.save({
      data: newState,
      pathname: CACHE_ACTIONS.marketing,
    });

    return newState;
  }
};

function boardsReducer(state = defaultState, action) {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export default boardsReducer;